import React, {
    useEffect,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    FiAlertTriangle,
    FiBriefcase,
    FiRefreshCw,
    FiSave,
    FiSettings,
    FiShield,
    FiSliders,
} from "react-icons/fi";

import useSwal
    from "../../../../../hooks/useSwal";


import {
    fetchPlatformSettings,
    updatePlatformSettings,

    selectPlatformSettings,
    selectPlatformSettingsLoading,
    selectPlatformSettingsUpdating,
} from "../../../../../store/platformSettings/platformSettingsSlice";

import "./PlatformSettings.css";


const EMPTY_FORM = {

    platformName:
        "",

    platformTagline:
        "",

    supportEmail:
        "",

    supportMobile:
        "",

    websiteUrl:
        "",

    defaultBusinessStatus:
        "ACTIVE",

    defaultSubscriptionStatus:
        "TRIAL",

    defaultTrialDays:
        30,

    defaultStorefrontEnabled:
        true,

    defaultMarketplaceEnabled:
        true,

    maxLoginAttempts:
        5,

    accountLockMinutes:
        15,

    requireStrongPassword:
        true,

    sessionTimeoutMinutes:
        120,

    allowPlatformBusinessCreation:
        true,

    allowTenantSelfRegistration:
        false,

    maintenanceMode:
        false,

    maintenanceMessage:
        "",

    auditLogEnabled:
        true,

    auditRetentionDays:
        365,
};


/*
|--------------------------------------------------------------------------
| SWITCH
|--------------------------------------------------------------------------
*/

const SettingSwitch = ({
    name,
    checked,
    title,
    description,
    onChange,
    disabled,
}) => {

    return (

        <label className="platform-settings-switch-row">

            <div>

                <strong>
                    {title}
                </strong>

                <span>
                    {description}
                </span>

            </div>


            <div className="platform-settings-switch">

                <input
                    type="checkbox"
                    name={name}
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                />

                <span />

            </div>

        </label>

    );

};


/*
|--------------------------------------------------------------------------
| PLATFORM SETTINGS
|--------------------------------------------------------------------------
*/

const PlatformSettings =
    () => {

        const dispatch =
            useDispatch();


        const swal =
            useSwal();


        const settings =
            useSelector(
                selectPlatformSettings
            );


        const loading =
            useSelector(
                selectPlatformSettingsLoading
            );


        const updating =
            useSelector(
                selectPlatformSettingsUpdating
            );


        const [
            form,
            setForm,
        ] =
            useState(
                EMPTY_FORM
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformSettings()
                );

            },
            [
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SYNC
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                if (
                    !settings
                ) {

                    return;

                }


                setForm({
                    ...EMPTY_FORM,
                    ...settings,
                });

            },
            [
                settings,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | CHANGE
        |--------------------------------------------------------------------------
        */

        const handleChange =
            (
                event
            ) => {

                const {
                    name,
                    value,
                    checked,
                    type,
                } =
                    event.target;


                setForm(
                    (
                        previous
                    ) => ({
                        ...previous,

                        [name]:
                            type ===
                            "checkbox"
                                ? checked
                                : type ===
                                    "number"
                                    ? Number(
                                        value
                                    )
                                    : value,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {

                dispatch(
                    fetchPlatformSettings()
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SAVE
        |--------------------------------------------------------------------------
        */

        const handleSubmit =
            async (
                event
            ) => {

                event.preventDefault();


                const result =
                    await dispatch(
                        updatePlatformSettings(
                            form
                        )
                    );


                if (
                    updatePlatformSettings
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    swal.success({
                        title:
                            "Settings Saved",

                        text:
                            "Platform settings updated successfully.",
                    });


                    return;

                }


                swal.error({
                    title:
                        "Update Failed",

                    text:
                        result
                            ?.payload
                            ?.message ||
                        "Unable to update platform settings.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | LOADING
        |--------------------------------------------------------------------------
        */

        if (
            loading &&
            !settings
        ) {

            return (


                    <div className="platform-settings-loading">

                        <div className="platform-settings-loader" />

                        <strong>
                            Loading Platform Settings
                        </strong>

                        <span>
                            Fetching global platform configuration...
                        </span>

                    </div>


            );

        }


        return (


                <form
                    className="platform-settings-page"
                    onSubmit={
                        handleSubmit
                    }
                >

                    {/*
                    |--------------------------------------------------------------------------
                    | HEADER
                    |--------------------------------------------------------------------------
                    */}

                    <header className="platform-settings-header">

                        <div>

                            <div className="platform-settings-eyebrow">

                                <FiSettings />

                                SYSTEM CONFIGURATION

                            </div>


                            <h1>
                                Platform Settings
                            </h1>


                            <p>
                                Manage global business defaults, platform security and operational settings.
                            </p>

                        </div>


                        <div className="platform-settings-header-actions">

                            <button
                                type="button"
                                onClick={
                                    handleRefresh
                                }
                                disabled={
                                    loading ||
                                    updating
                                }
                            >

                                <FiRefreshCw />

                                Refresh

                            </button>


                            <button
                                type="submit"
                                className="primary"
                                disabled={
                                    updating
                                }
                            >

                                <FiSave />

                                {
                                    updating
                                        ? "Saving..."
                                        : "Save Changes"
                                }

                            </button>

                        </div>

                    </header>


                    {/*
                    |--------------------------------------------------------------------------
                    | PLATFORM INFORMATION
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-settings-card">

                        <div className="platform-settings-card-header">

                            <div>
                                <FiSettings />
                            </div>


                            <section>

                                <h2>
                                    Platform Information
                                </h2>

                                <p>
                                    Basic information displayed across the Super Admin platform.
                                </p>

                            </section>

                        </div>


                        <div className="platform-settings-grid">

                            <label>

                                <span>
                                    Platform Name
                                </span>

                                <input
                                    name="platformName"
                                    value={
                                        form.platformName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>


                            <label>

                                <span>
                                    Platform Tagline
                                </span>

                                <input
                                    name="platformTagline"
                                    value={
                                        form.platformTagline
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>


                            <label>

                                <span>
                                    Support Email
                                </span>

                                <input
                                    type="email"
                                    name="supportEmail"
                                    value={
                                        form.supportEmail
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>


                            <label>

                                <span>
                                    Support Mobile
                                </span>

                                <input
                                    name="supportMobile"
                                    value={
                                        form.supportMobile
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>


                            <label className="full">

                                <span>
                                    Website URL
                                </span>

                                <input
                                    name="websiteUrl"
                                    value={
                                        form.websiteUrl
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="https://www.example.com"
                                />

                            </label>

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | BUSINESS DEFAULTS
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-settings-card">

                        <div className="platform-settings-card-header">

                            <div>
                                <FiBriefcase />
                            </div>


                            <section>

                                <h2>
                                    Business Defaults
                                </h2>

                                <p>
                                    Default configuration applied to newly created businesses.
                                </p>

                            </section>

                        </div>


                        <div className="platform-settings-grid">

                            <label>

                                <span>
                                    Default Business Status
                                </span>

                                <select
                                    name="defaultBusinessStatus"
                                    value={
                                        form.defaultBusinessStatus
                                    }
                                    onChange={
                                        handleChange
                                    }
                                >

                                    <option value="ACTIVE">
                                        Active
                                    </option>

                                    <option value="INACTIVE">
                                        Inactive
                                    </option>

                                </select>

                            </label>


                            <label>

                                <span>
                                    Subscription Status
                                </span>

                                <select
                                    name="defaultSubscriptionStatus"
                                    value={
                                        form.defaultSubscriptionStatus
                                    }
                                    onChange={
                                        handleChange
                                    }
                                >

                                    <option value="TRIAL">
                                        Trial
                                    </option>

                                    <option value="ACTIVE">
                                        Active
                                    </option>

                                </select>

                            </label>


                            <label>

                                <span>
                                    Default Trial Days
                                </span>

                                <input
                                    type="number"
                                    min="0"
                                    max="365"
                                    name="defaultTrialDays"
                                    value={
                                        form.defaultTrialDays
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>

                        </div>


                        <div className="platform-settings-switch-list">

                            <SettingSwitch
                                name="defaultStorefrontEnabled"
                                title="Storefront Enabled"
                                description="Enable storefront by default for newly created businesses."
                                checked={
                                    form.defaultStorefrontEnabled
                                }
                                onChange={
                                    handleChange
                                }
                            />


                            <SettingSwitch
                                name="defaultMarketplaceEnabled"
                                title="Marketplace Enabled"
                                description="Enable marketplace access for new businesses."
                                checked={
                                    form.defaultMarketplaceEnabled
                                }
                                onChange={
                                    handleChange
                                }
                            />


                            <SettingSwitch
                                name="allowPlatformBusinessCreation"
                                title="Allow Business Creation"
                                description="Allow Platform Super Admins to create new businesses."
                                checked={
                                    form.allowPlatformBusinessCreation
                                }
                                onChange={
                                    handleChange
                                }
                            />


                            <SettingSwitch
                                name="allowTenantSelfRegistration"
                                title="Tenant Self Registration"
                                description="Allow businesses to register themselves without Platform Admin creation."
                                checked={
                                    form.allowTenantSelfRegistration
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | SECURITY
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-settings-card">

                        <div className="platform-settings-card-header">

                            <div>
                                <FiShield />
                            </div>


                            <section>

                                <h2>
                                    Security
                                </h2>

                                <p>
                                    Configure login protection and account security defaults.
                                </p>

                            </section>

                        </div>


                        <div className="platform-settings-grid">

                            <label>

                                <span>
                                    Maximum Login Attempts
                                </span>

                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    name="maxLoginAttempts"
                                    value={
                                        form.maxLoginAttempts
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </label>


                            <label>

                                <span>
                                    Account Lock Duration
                                </span>

                                <div className="platform-settings-input-suffix">

                                    <input
                                        type="number"
                                        name="accountLockMinutes"
                                        value={
                                            form.accountLockMinutes
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    <span>
                                        minutes
                                    </span>

                                </div>

                            </label>


                            <label>

                                <span>
                                    Session Timeout
                                </span>

                                <div className="platform-settings-input-suffix">

                                    <input
                                        type="number"
                                        name="sessionTimeoutMinutes"
                                        value={
                                            form.sessionTimeoutMinutes
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    <span>
                                        minutes
                                    </span>

                                </div>

                            </label>

                        </div>


                        <div className="platform-settings-switch-list">

                            <SettingSwitch
                                name="requireStrongPassword"
                                title="Require Strong Password"
                                description="Require stronger passwords for tenant administrative users."
                                checked={
                                    form.requireStrongPassword
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | AUDIT
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-settings-card">

                        <div className="platform-settings-card-header">

                            <div>
                                <FiSliders />
                            </div>


                            <section>

                                <h2>
                                    Audit & Logging
                                </h2>

                                <p>
                                    Control platform activity logging and retention.
                                </p>

                            </section>

                        </div>


                        <div className="platform-settings-switch-list">

                            <SettingSwitch
                                name="auditLogEnabled"
                                title="Platform Audit Logging"
                                description="Record security-sensitive Super Admin operations."
                                checked={
                                    form.auditLogEnabled
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>


                        <div className="platform-settings-grid single">

                            <label>

                                <span>
                                    Audit Retention
                                </span>

                                <div className="platform-settings-input-suffix">

                                    <input
                                        type="number"
                                        name="auditRetentionDays"
                                        value={
                                            form.auditRetentionDays
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    <span>
                                        days
                                    </span>

                                </div>

                            </label>

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | MAINTENANCE
                    |--------------------------------------------------------------------------
                    */}

                    <section
                        className={`platform-settings-card ${
                            form.maintenanceMode
                                ? "maintenance-active"
                                : ""
                        }`}
                    >

                        <div className="platform-settings-card-header">

                            <div>
                                <FiAlertTriangle />
                            </div>


                            <section>

                                <h2>
                                    Maintenance Mode
                                </h2>

                                <p>
                                    Temporarily restrict tenant access during maintenance.
                                </p>

                            </section>

                        </div>


                        <div className="platform-settings-switch-list">

                            <SettingSwitch
                                name="maintenanceMode"
                                title="Enable Maintenance Mode"
                                description="Tenant access can be restricted while platform maintenance is active."
                                checked={
                                    form.maintenanceMode
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>


                        <div className="platform-settings-grid single">

                            <label className="full">

                                <span>
                                    Maintenance Message
                                </span>

                                <textarea
                                    name="maintenanceMessage"
                                    value={
                                        form.maintenanceMessage
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    rows="4"
                                />

                            </label>

                        </div>

                    </section>

                </form>


        );

    };


export default PlatformSettings;